import IFamilyTree, {
  IFamilyTreeNodeRelation,
  IFamilyTreeNode,
} from "../interfaces/IFamilyTree";
import * as _ from "underscore";
import { v4 as uuidV4 } from "uuid";
import { IFamilyTreePerson } from "../interfaces";

export default class TreeHelper {
  private editMode: boolean;
  private tree: IFamilyTree;
  private _nodes: IFamilyTreeNode[] = [];

  constructor(treeData: IFamilyTree, editMode?: boolean) {
    this.tree = treeData;
    this.editMode = editMode === true;

    this.generateNodes();

    if (this.editMode) {
      this.injectSuggestions();
    }
  }

  public get nodes(): IFamilyTreeNode[] {
    return this._nodes;
  }

  /**
   * Update a person node
   * @param personId person id
   * @param values values to update
   */
  updatePersonNode(personId: string, values: Partial<IFamilyTreeNode>) {
    const index = this._nodes.findIndex(
      (item) => item.id.toString() === personId
    );
    if (index !== -1) {
      this._nodes[index].id = values.id ?? this._nodes[index].id;
      this._nodes[index].children =
        values.children ?? this._nodes[index].children;
      this._nodes[index].gender = values.gender ?? this._nodes[index].gender;
      this._nodes[index].parents = values.parents ?? this._nodes[index].parents;
      this._nodes[index].siblings =
        values.siblings ?? this._nodes[index].siblings;
      this._nodes[index].spouses = values.spouses ?? this._nodes[index].spouses;
      this._nodes[index].suggestion =
        values.suggestion ?? this._nodes[index].suggestion;
    }
  }

  /**
   * Get a person by ID
   * @param personId person ID
   */
  getPerson(personId: number): IFamilyTreePerson | undefined {
    return this.tree.Person_set.find((item) => item.id === personId);
  }

  /**
   * Get a person node
   * @param nodeId person id
   */
  getPersonNode(nodeId: string): IFamilyTreeNode | undefined {
    return this._nodes.find((item) => item.id === nodeId);
  }

  /**
   * Generate a person family tree node
   * @param personId person ID
   */
  generatePersonNode(personId: number): IFamilyTreeNode | undefined {
    const person = this.tree.Person_set.find((item) => item.id === personId);
    if (person) {
      return {
        children: this.findChildren(person.id),
        gender: person.gender,
        id: person.id.toString(),
        parents: this.findParents(person.id),
        siblings: this.findSiblings(person.id),
        spouses: this.findSpouses(person.id),
      };
    } else {
      return undefined;
    }
  }

  /**
   * Generate nodes
   */
  private generateNodes() {
    for (let index = 0; index < this.tree.Person_set.length; index++) {
      const person = this.tree.Person_set[index];

      this._nodes.push({
        children: this.findChildren(person.id),
        gender: person.gender,
        id: person.id.toString(),
        parents: this.findParents(person.id),
        siblings: this.findSiblings(person.id),
        spouses: this.findSpouses(person.id),
        person: person,
      });
    }
  }

  /**
   * Inject suggestions
   */
  private injectSuggestions() {
    for (let index = 0; index < this.tree.Person_set.length; index++) {
      const person = this.tree.Person_set[index];
      const parents = this.findParents(person.id);
      const spouses = this.findSpouses(person.id);

      /**
       * Add suggestions in edit mode
       */
      if (this.editMode) {
        /**
         * Parents suggestions
         */
        if (parents.length !== 2) {
          switch (parents.length) {
            case 0: {
              const fatherId = uuidV4();
              const motherId = uuidV4();

              this.updatePersonNode(person.id.toString(), {
                parents: [
                  {
                    id: fatherId,
                    type: "blood",
                  },
                  {
                    id: motherId,
                    type: "blood",
                  },
                ],
              });

              this._nodes.push({
                id: fatherId,
                children: [
                  {
                    id: person.id.toString(),
                    type: "blood",
                  },
                ],
                gender: "male",
                parents: [],
                siblings: [],
                spouses: [
                  {
                    id: motherId,
                    type: "married",
                  },
                ],
                suggestion: {
                  relationType: "parent",
                  personInvolved: [
                    {
                      relation: "parent",
                      relationType: "to",
                      relationWith: person.id,
                    },
                  ],
                },
              });

              this._nodes.push({
                id: motherId,
                children: [
                  {
                    id: person.id.toString(),
                    type: "blood",
                  },
                ],
                gender: "female",
                parents: [],
                siblings: [],
                spouses: [
                  {
                    id: fatherId,
                    type: "married",
                  },
                ],
                suggestion: {
                  relationType: "parent",
                  personInvolved: [
                    {
                      relation: "parent",
                      relationType: "to",
                      relationWith: person.id,
                    },
                  ],
                },
              });
              break;
            }
            case 1: {
              const uniqueParent = this.getPerson(Number(parents[0].id));
              const parentId = uuidV4();

              /**
               * Update the person parent
               */
              this.updatePersonNode(person.id.toString(), {
                parents: [
                  ...this.findParents(person.id),
                  {
                    id: parentId,
                    type: "blood",
                  },
                ],
              });

              /**
               * The unique parent exist
               */
              if (uniqueParent) {
                /**
                 * Update the unique parent spouses
                 */
                this.updatePersonNode(uniqueParent.id.toString(), {
                  spouses: [
                    ...this.findSpouses(uniqueParent.id),
                    {
                      id: parentId.toString(),
                      type: "married",
                    },
                  ],
                });

                this._nodes.push({
                  id: parentId,
                  children: [
                    {
                      id: person.id.toString(),
                      type: "blood",
                    },
                  ],
                  gender: uniqueParent.gender === "male" ? "female" : "male",
                  parents: [],
                  siblings: [],
                  spouses: [
                    {
                      id: uniqueParent.id.toString(),
                      type: "married",
                    },
                  ],
                  suggestion: {
                    relationType: "parent",
                    personInvolved: [
                      {
                        relation: "parent",
                        relationType: "to",
                        relationWith: person.id,
                      },
                      {
                        relation: "partner",
                        relationType: "to",
                        relationWith: uniqueParent.id,
                      },
                    ],
                  },
                });
              }
              break;
            }
          }
        } else {
          /**
           * Inject spouses if parents are not partners
           */
        }

        /**
         * Spouse suggestion
         */
        if (this.findSpouses(person.id).length === 0) {
          const spouseId = uuidV4();

          this.updatePersonNode(person.id.toString(), {
            spouses: [
              ...this.findSpouses(person.id),
              {
                id: spouseId,
                type: "married",
              },
            ],
          });

          this._nodes.push({
            id: spouseId,
            children: [],
            gender: person.gender === "male" ? "female" : "male",
            parents: [],
            siblings: [],
            spouses: [
              {
                id: person.id.toString(),
                type: "married",
              },
            ],
            suggestion: {
              relationType: "partner",
              personInvolved: [
                {
                  relation: "partner",
                  relationType: "to",
                  relationWith: person.id,
                },
              ],
            },
          });
        }

        /**
         * Child suggestion
         */
        if (spouses.length > 0) {
          for (const spouse of spouses) {
            const spouseNode = this.getPersonNode(spouse.id);
            console.log("Spouse id", spouseNode?.id);
            if (
              spouseNode &&
              !spouseNode.suggestion &&
              !this.hasChildSuggestion(person.id.toString(), spouse.id)
            ) {
              const childId = uuidV4();
              console.log(`child suggestion for ${person.id} and ${spouse.id}`);
              this.updatePersonNode(person.id.toString(), {
                children: [
                  ...this.findChildren(person.id),
                  {
                    id: childId,
                    type: "blood",
                  },
                ],
              });

              this.updatePersonNode(spouse.id, {
                children: [
                  ...this.findChildren(Number(spouse.id)),
                  {
                    id: childId,
                    type: "blood",
                  },
                ],
              });

              this._nodes.push({
                id: childId,
                children: [],
                gender: "unknown",
                parents: [
                  {
                    id: person.id.toString(),
                    type: "blood",
                  },
                  {
                    id: spouse.id,
                    type: "blood",
                  },
                ],
                siblings: [],
                spouses: [],
                suggestion: {
                  relationType: "child",
                  personInvolved: [
                    {
                      relation: "parent",
                      relationType: "from",
                      relationWith: person.id,
                    },
                    {
                      relation: "parent",
                      relationType: "from",
                      relationWith: spouse.id,
                    },
                  ],
                },
              });
            }
          }
        }
      }
    }

    if (this._nodes.length === 0) {
      this._nodes.push({
        id: uuidV4(),
        children: [],
        gender: "unknown",
        parents: [],
        siblings: [],
        spouses: [],
        suggestion: {
          relationType: "unknown",
          personInvolved: [],
        },
      });
    }
  }

  /**
   * Check if two person have child suggestion
   * @param firstPartner first parent ID
   * @param secondPartner second parent ID
   */
  private hasChildSuggestion(
    firstPartner: string,
    secondPartner: string
  ): boolean {
    for (const node of this._nodes) {
      if (
        node.suggestion &&
        _.pluck(node.parents, "id").includes(firstPartner) &&
        _.pluck(node.parents, "id").includes(secondPartner)
      ) {
        return true;
      }
    }
    return false;
  }

  private hasSpouseSuggestion(personId: string): boolean {
    for (const node of this._nodes) {
      if (node.suggestion && _.pluck(node.spouses, "id").includes(personId)) {
        return true;
      }
    }
    return false;
  }

  /**
   * Find person's parents
   * @param personId person ID
   */
  private findParents(
    personId: number
  ): ReadonlyArray<IFamilyTreeNodeRelation> {
    const r: Array<IFamilyTreeNodeRelation> = [];
    for (let index = 0; index < this.tree.Relation_set.length; index++) {
      const relation = this.tree.Relation_set[index];
      if (
        relation.relation_to === personId &&
        relation.relation === "parent" &&
        !_.pluck(r, "id").includes(relation.relation_from.toString())
      ) {
        r.push({
          id: relation.relation_from.toString(),
          type: "blood",
        });
      }
    }
    return r;
  }

  /**
   * Find a person children
   * @param personId person ID
   */
  private findChildren(
    personId: number
  ): ReadonlyArray<IFamilyTreeNodeRelation> {
    const r: Array<IFamilyTreeNodeRelation> = [];
    for (let index = 0; index < this.tree.Relation_set.length; index++) {
      const relation = this.tree.Relation_set[index];
      if (
        relation.relation_from === personId &&
        relation.relation === "parent" &&
        !_.pluck(r, "id").includes(relation.relation_to.toString())
      ) {
        r.push({
          id: relation.relation_to.toString(),
          type: "blood",
        });
      }
    }
    return r;
  }

  /**
   * Find a person's spouses
   * @param personId person id
   */
  private findSpouses(
    personId: number
  ): ReadonlyArray<IFamilyTreeNodeRelation> {
    const r: Array<IFamilyTreeNodeRelation> = [];
    for (let index = 0; index < this.tree.Relation_set.length; index++) {
      const relation = this.tree.Relation_set[index];
      if (
        relation.relation_from === personId &&
        relation.relation === "partner" &&
        !_.pluck(r, "id").includes(relation.relation_to.toString())
      ) {
        r.push({
          id: relation.relation_to.toString(),
          type: "married",
        });
      } else if (
        relation.relation_to === personId &&
        relation.relation === "partner" &&
        !_.pluck(r, "id").includes(relation.relation_from.toString())
      ) {
        r.push({
          id: relation.relation_from.toString(),
          type: "married",
        });
      }
    }
    return r;
  }

  /**
   * Find a person's siblings
   * @param personId person id
   */
  private findSiblings(
    personId: number
  ): ReadonlyArray<IFamilyTreeNodeRelation> {
    // get the person parents
    const parents = this.findParents(personId);
    const r: Array<IFamilyTreeNodeRelation> = [];
    for (let index = 0; index < this.tree.Relation_set.length; index++) {
      const relation = this.tree.Relation_set[index];
      if (
        relation.relation === "parent" &&
        _.pluck(parents, "id").includes(relation.relation_from.toString()) &&
        relation.relation_to !== personId &&
        !_.pluck(r, "id").includes(relation.relation_to.toString())
      ) {
        r.push({
          id: relation.relation_to.toString(),
          type: "blood",
        });
      }
    }
    return r;
  }
}
